import {
  Calendar,
  MessageCircle,
  Users,
  Send,
  CreditCard,
  Scan,
  AlertCircle,
  ShoppingCart,
  Cloud,
  FileText,
  BarChart3,
  DollarSign,
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import MessageCreationTool from '../../MessageCenter/MessageCreationTool';

const EventsManager = () => {
  const [ticketsSold, setTicketsSold] = useState(8547);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);

  // Simulate real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      setTicketsSold((prev) => prev + Math.floor(Math.random() * 5));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleActionClick = (actionName) => {
    // eslint-disable-next-line no-console
    console.log(`Navigating to: ${actionName}`);
    // In real implementation, this would handle navigation
  };

  // Messaging opportunity for Quick Send
  const eventMessagingOpportunity = {
    id: 'event-update',
    type: 'event-update',
    title: 'Event Update Notification',
    subtitle: `${ticketsSold.toLocaleString()} customers need event updates`,
    icon: <MessageCircle className="w-5 h-5" />,
    audience: ticketsSold,
    smsAudience: ticketsSold,
    pushAudience: Math.floor(ticketsSold * 0.7), // ~70% have the app
    estimatedRevenue: '$0',
    timing: 'immediate',
    event: 'Market USA 100',
    recommendedChannel: 'sms',
    messages: {
      // eslint-disable-next-line jsx-a11y/accessible-emoji
      sms: '🏁 Market USA 100 Update: Gates open at 5PM today! Weather looks perfect for racing. Get directions: https://tkt.link/market-usa-100',
      // eslint-disable-next-line jsx-a11y/accessible-emoji
      push: '🏁 Market USA 100 starts soon! Gates open at 5PM. Perfect weather for racing today!',
    },
  };

  const handleQuickSend = () => {
    // eslint-disable-next-line no-console
    console.log(
      'Opening quick message composer with opportunity:',
      eventMessagingOpportunity
    );
    setSelectedOpportunity(eventMessagingOpportunity);
    setShowMessageModal(true);
  };

  const handleSendComplete = (messageData) => {
    // eslint-disable-next-line no-console
    console.log('Message sent successfully:', messageData);
    setShowMessageModal(false);
    setSelectedOpportunity(null);
    // Here you could show a success notification or refresh data
  };

  const handleModalClose = () => {
    setShowMessageModal(false);
    setSelectedOpportunity(null);
  };

  const handleQuickScan = () => {
    // eslint-disable-next-line no-console
    console.log('Quick scan mode activated');
    // In real implementation, this would activate scan mode
  };

  const styles = {
    container: {
      fontFamily: 'Arial, sans-serif',
      background: '#f5f5f5',
      color: '#333333',
      lineHeight: 1.6,
      minHeight: '100vh',
    },
    header: {
      background: '#ffffff',
      padding: '1.5rem 2rem',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      borderTop: '4px solid #fa4616',
    },
    headerContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      maxWidth: '1400px',
      margin: '0 auto',
    },
    eventInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '2rem',
    },
    eventTitle: {
      fontSize: '2rem',
      fontWeight: 'bold',
      marginBottom: '0.25rem',
      color: '#222222',
    },
    eventMeta: {
      display: 'flex',
      gap: '2rem',
      fontSize: '0.9rem',
      color: '#666666',
      alignItems: 'center',
    },
    venueLogo: {
      width: '120px',
      height: '60px',
      background: '#f0f0f0',
      border: '1px solid #dddddd',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      fontSize: '0.8rem',
      color: '#999999',
    },
    messagingPrompt: {
      background: '#f8f9fa',
      padding: '1rem 2rem',
      borderBottom: '1px solid #e9ecef',
    },
    promptContainer: {
      maxWidth: '1400px',
      margin: '0 auto',
      width: '100%',
      boxSizing: 'border-box',
    },
    promptBanner: {
      background: 'white',
      borderRadius: '12px',
      padding: '1.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '1.5rem',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
    },
    promptIconWrapper: {
      width: '56px',
      height: '56px',
      background: '#e8f0fe',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    promptDetails: {
      flex: 1,
    },
    promptHeader: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#1a1a1a',
      margin: '0 0 0.375rem 0',
      lineHeight: 1.3,
    },
    promptSubtext: {
      fontSize: '0.875rem',
      color: '#666',
      margin: '0 0 0.75rem 0',
      lineHeight: 1.4,
    },
    promptMeta: {
      display: 'flex',
      gap: '1.5rem',
      alignItems: 'center',
    },
    metaItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.875rem',
      color: '#666',
    },
    quickSendBtn: {
      background: '#fa4616',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '0.875rem 1.5rem',
      fontWeight: 600,
      fontSize: '0.875rem',
      letterSpacing: '0.5px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      whiteSpace: 'nowrap',
    },
    promptBannerMobile: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: '16px',
    },
    mobileHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    mobileTitle: {
      fontSize: '18px',
      fontWeight: 600,
      color: '#1f2937',
      margin: 0,
      lineHeight: 1.3,
    },
    mobileSubtitle: {
      fontSize: '14px',
      color: '#6b7280',
      margin: 0,
      lineHeight: 1.4,
    },
    mobileStats: {
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      flexWrap: 'wrap',
    },
    mobileStatItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '14px',
      color: '#6b7280',
    },
    mobileRevenueIcon: {
      fontSize: '16px',
    },
    mobileRevenueText: {
      color: '#10b981',
      fontWeight: 500,
    },
    mobileQuickSendBtn: {
      background: '#fa4616',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '14px 20px',
      fontWeight: 600,
      fontSize: '14px',
      letterSpacing: '0.5px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      width: '100%',
      textTransform: 'uppercase',
    },
    statsContainer: {
      background: '#ffffff',
      padding: '2rem',
      borderBottom: '1px solid #e0e0e0',
    },
    statsWrapper: {
      maxWidth: '1400px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
      gap: '1.5rem',
    },
    statCard: {
      background: '#fafafa',
      padding: '1.5rem',
      borderRadius: '8px',
      border: '1px solid #e0e0e0',
      transition: 'all 0.2s ease',
      position: 'relative',
      overflow: 'hidden',
    },
    statValue: {
      fontSize: '2rem',
      fontWeight: 'bold',
      color: '#fa4616',
      marginBottom: '0.25rem',
    },
    statLabel: {
      color: '#666666',
      fontSize: '0.875rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    statChange: {
      fontSize: '0.875rem',
      color: '#666666',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
      marginTop: '0.5rem',
    },
    actionsContainer: {
      padding: '2rem',
      maxWidth: '1400px',
      margin: '0 auto',
    },
    sectionTitle: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: '#222222',
      marginBottom: '2rem',
      paddingBottom: '0.5rem',
      borderBottom: '2px solid #fa4616',
      display: 'inline-block',
    },
    actionsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '1.5rem',
    },
    actionCard: {
      background: '#ffffff',
      borderRadius: '8px',
      padding: '2rem',
      border: '1px solid #e0e0e0',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      position: 'relative',
    },
    actionIcon: {
      width: '48px',
      height: '48px',
      background: '#fff5f2',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '1rem',
      transition: 'all 0.2s ease',
    },
    actionTitle: {
      fontSize: '1.125rem',
      fontWeight: 'bold',
      marginBottom: '0.5rem',
      color: '#222222',
    },
    actionDescription: {
      color: '#666666',
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    quickActions: {
      position: 'fixed',
      bottom: '2rem',
      right: '2rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    },
    quickActionBtn: {
      width: '56px',
      height: '56px',
      background: '#fa4616',
      color: 'white',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.5rem',
      boxShadow: '0 4px 12px rgba(250, 70, 22, 0.3)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      border: 'none',
    },
  };

  const actionItems = [
    {
      icon: <Scan size={24} />,
      title: 'Scan Tickets',
      description: 'Enter scan mode to validate tickets at entry points',
    },
    {
      icon: <Users size={24} />,
      title: 'Ticket Holders',
      description: 'Manage and view all event ticket holders',
    },
    {
      icon: <CreditCard size={24} />,
      title: 'Point of Sale',
      description: 'Process on-site ticket sales and transactions',
    },
    {
      icon: <AlertCircle size={24} />,
      title: 'Cancel / Postpone',
      description: 'Manage event cancellations or schedule changes',
    },
    {
      icon: <ShoppingCart size={24} />,
      title: 'Tickets on Sale',
      description: 'View and manage current ticket inventory',
    },
    {
      icon: <Cloud size={24} />,
      title: 'Weather Center',
      description: 'Monitor weather conditions and forecasts',
    },
    {
      icon: <FileText size={24} />,
      title: 'Guest List',
      description: 'Manage VIP and special guest access',
    },
    {
      icon: <BarChart3 size={24} />,
      title: 'Reporting',
      description: 'Generate detailed event analytics and reports',
    },
    {
      icon: <DollarSign size={24} />,
      title: 'Transactions',
      description: 'View all financial transactions and history',
    },
  ];

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.eventInfo}>
            <div>
              <h1 style={styles.eventTitle}>Market USA 100</h1>
              <div style={styles.eventMeta}>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <Calendar size={16} color="#666666" />
                  SAT JAN 18, 2025
                </span>
              </div>
            </div>
          </div>
          <div style={styles.venueLogo}>VENUE LOGO</div>
        </div>
      </header>

      {/* Messaging Prompt */}
      <div style={styles.messagingPrompt}>
        <div style={styles.promptContainer}>
          <div
            style={{
              ...styles.promptBanner,
              ...(window.innerWidth < 768 ? styles.promptBannerMobile : {}),
            }}
          >
            {window.innerWidth < 768 ? (
              // Mobile Layout - Card Style
              <>
                <div style={styles.mobileHeader}>
                  <Calendar size={24} color="#4285f4" />
                  <h3 style={styles.mobileTitle}>
                    Need to update these {ticketsSold.toLocaleString()}{' '}
                    customers?
                  </h3>
                </div>
                <p style={styles.mobileSubtitle}>
                  Send weather alerts, schedule changes, or concession
                  promotions to ticket holders
                </p>
                <div style={styles.mobileStats}>
                  <span style={styles.mobileStatItem}>
                    <Users size={16} color="#6b7280" />
                    {ticketsSold.toLocaleString()} recipients
                  </span>
                  <span style={styles.mobileStatItem}>
                    {/* eslint-disable-next-line */}
                    <span style={styles.mobileRevenueIcon}>📈</span>
                    <span style={styles.mobileRevenueText}>
                      Est. $0 revenue
                    </span>
                  </span>
                  <span style={styles.mobileStatItem}>
                    <MessageCircle size={16} color="#6b7280" />
                    SMS
                  </span>
                </div>
                <button
                  style={styles.mobileQuickSendBtn}
                  onClick={handleQuickSend}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#e53e16';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#fa4616';
                  }}
                >
                  <Send size={18} />
                  QUICK SEND
                </button>
              </>
            ) : (
              // Desktop Layout - Current Design
              <>
                <div style={styles.promptIconWrapper}>
                  <MessageCircle size={28} color="#4285f4" />
                </div>
                <div style={styles.promptDetails}>
                  <h3 style={styles.promptHeader}>
                    Need to update these {ticketsSold.toLocaleString()}{' '}
                    customers?
                  </h3>
                  <p style={styles.promptSubtext}>
                    Send weather alerts, schedule changes, or concession
                    promotions to ticket holders
                  </p>
                  <div style={styles.promptMeta}>
                    <span style={styles.metaItem}>
                      <Users size={16} color="#999" />
                      {ticketsSold.toLocaleString()} recipients
                    </span>
                    <span style={styles.metaItem}>
                      <MessageCircle size={16} color="#999" />
                      SMS
                    </span>
                  </div>
                </div>
                <button
                  style={styles.quickSendBtn}
                  onClick={handleQuickSend}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#e53e16';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow =
                      '0 4px 12px rgba(250, 70, 22, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#fa4616';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <Send size={18} />
                  QUICK SEND
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <section style={styles.statsContainer}>
        <div style={styles.statsWrapper}>
          <div
            style={styles.statCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#ffffff';
              e.currentTarget.style.borderColor = '#fa4616';
              e.currentTarget.style.boxShadow =
                '0 2px 8px rgba(250, 70, 22, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#fafafa';
              e.currentTarget.style.borderColor = '#e0e0e0';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={styles.statValue}>{ticketsSold.toLocaleString()}</div>
            <div style={styles.statLabel}>Tickets Sold</div>
            <div style={{ ...styles.statChange, color: '#2e7d32' }}>
              ↑ 12% from last event
            </div>
          </div>

          <div
            style={styles.statCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#ffffff';
              e.currentTarget.style.borderColor = '#fa4616';
              e.currentTarget.style.boxShadow =
                '0 2px 8px rgba(250, 70, 22, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#fafafa';
              e.currentTarget.style.borderColor = '#e0e0e0';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={styles.statValue}>$245K</div>
            <div style={styles.statLabel}>Total Revenue</div>
            <div style={{ ...styles.statChange, color: '#2e7d32' }}>
              ↑ 8% from last event
            </div>
          </div>

          <div
            style={styles.statCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#ffffff';
              e.currentTarget.style.borderColor = '#fa4616';
              e.currentTarget.style.boxShadow =
                '0 2px 8px rgba(250, 70, 22, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#fafafa';
              e.currentTarget.style.borderColor = '#e0e0e0';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={styles.statValue}>82%</div>
            <div style={styles.statLabel}>Capacity</div>
            <div style={{ ...styles.statChange, color: '#2e7d32' }}>
              ↑ 5% from last hour
            </div>
          </div>

          <div
            style={styles.statCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#ffffff';
              e.currentTarget.style.borderColor = '#fa4616';
              e.currentTarget.style.boxShadow =
                '0 2px 8px rgba(250, 70, 22, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#fafafa';
              e.currentTarget.style.borderColor = '#e0e0e0';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={styles.statValue}>1,453</div>
            <div style={styles.statLabel}>Check-ins Today</div>
            <div style={{ ...styles.statChange, color: '#1976d2' }}>
              7,094 tickets remaining
            </div>
          </div>
        </div>
      </section>

      {/* Actions Section */}
      <main style={styles.actionsContainer}>
        <h2 style={styles.sectionTitle}>Event Management</h2>
        <div style={styles.actionsGrid}>
          {actionItems.map((action, index) => {
            const isHovered = hoveredCard === index;
            return (
              <div
                key={index}
                style={{
                  ...styles.actionCard,
                  transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
                  boxShadow: isHovered
                    ? '0 4px 12px rgba(250, 70, 22, 0.15)'
                    : 'none',
                  borderColor: isHovered ? '#fa4616' : '#e0e0e0',
                }}
                onClick={() => handleActionClick(action.title)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleActionClick(action.title);
                  }
                }}
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
                role="button"
                tabIndex={0}
              >
                <div
                  className="action-icon"
                  style={{
                    ...styles.actionIcon,
                    background: isHovered ? '#fa4616' : '#fff5f2',
                    transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                  }}
                >
                  {React.cloneElement(action.icon, {
                    color: isHovered ? 'white' : '#fa4616',
                  })}
                </div>
                <h3 style={styles.actionTitle}>{action.title}</h3>
                <p style={styles.actionDescription}>{action.description}</p>
              </div>
            );
          })}
        </div>
      </main>

      {/* Quick Actions */}
      <div style={styles.quickActions}>
        <button
          style={styles.quickActionBtn}
          onClick={handleQuickScan}
          title="Quick Scan"
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.1)';
            e.target.style.boxShadow = '0 6px 20px rgba(250, 70, 22, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.boxShadow = '0 4px 12px rgba(250, 70, 22, 0.3)';
          }}
        >
          <Scan size={24} />
        </button>
      </div>

      {/* Message Creation Modal */}
      {showMessageModal && selectedOpportunity && (
        <MessageCreationTool
          opportunity={selectedOpportunity}
          onClose={handleModalClose}
          onSend={handleSendComplete}
        />
      )}
    </div>
  );
};

export default EventsManager;
